/**
 * 74.3 step 0 (DECISIONS 341; ARCHITECTURE §5.39): the **world sheet** — what a planet's generated ground implies, derived
 * once per planet from the generated mesh alone and cached: the watershed (every vertex's way downhill, the flow that
 * gathers, the sinks and their basins, each basin's spill, the drainage chains, the lakes), a wet mask and an elevation
 * band, a climate cell per vertex (the biome field's input: wet or dry × low or high), and the chokepoint candidates
 * (saddles between basins, fords on the chains, the vertices most shortest paths cross). Nothing here is a height the
 * game reads: step 0 shows the sheet (the planet table) and measures it (the sweep); steps 1–4 put it to work.
 *
 * **The rule (lane F's condition):** the sheet is a function of the generated mesh only — `initialHeightmap` (the route
 * graph's weld, core/planetRoutes.ts, is built from it), the seed, the archetype and the wind axis; a crater, a dig or a
 * terrace never moves it (tests/worldSheet.test.ts). One weld: the route graph's unique vertices and CSR edges.
 *
 * **The watershed, on a sphere** (lane F's corrections): the vertices are taken in ascending height (ties by index) with
 * a union-find — a vertex with no lower neighbour starts a basin; where it touches a lower basin the higher one *spills*
 * at that height (a sphere has no boundary to flood from). A basin shallower than `pitFloor` under its spill is a pit of
 * the generator's jitter: filled to its spill (a hair's gradient toward it, so it drains), before any flow is counted.
 * Deeper basins keep their sink: they are the lakes, their level fixed at setup. Then every vertex sheds to its lowest
 * neighbour and the flow accumulates in height order (as core/forestDensity.ts does); edges with flow past `flowFrom`
 * are the drainage chains, each with its order (doublings over the threshold).
 *
 * Deterministic (dmath only; fixed orders). Cost: a few passes over the weld's vertices (2.5k–10k) and K² short walks
 * for the betweenness — measured by the sweep.
 */
import type { PlanetState, PlanetArchetype, Vec3 } from '../types';
import { planetRouteGraph, routeField, type PlanetRouteGraph } from './planetRoutes';
import { groundKey, spinAxisOf, windAxis } from './spinAxis';
import { dasin, datan2, dsin, dcos } from './dmath';
import { SeededRandom } from './random';
import { TERRAIN_PROFILES } from '../constants';
import { PERSON_BODY_HEIGHT, SCALE_PROFILES } from './scaleProfile';

/** 74.12 step 0: the spin axis and the ground's key live in core/spinAxis.ts (the wind reads them); kept here for their readers. */
export { groundKey, spinAxisOf };

export const WORLD_SHEET = {
  /** A basin shallower than this under its spill (fraction of the planet's radius), or than `pitJitter` × the profile's own per-vertex jitter height, is a pit of the jitter: filled. */
  pitFloorR: 0.004, pitJitter: 2,
  /** A basin at least this deep under its spill (fraction of R — 2 % is about four people on a large world; a shallower rule floods half of the plains — and this much past the pit floor) and this many vertices across holds a lake (W1) on a world whose own wetness is at least `lakeWetness`; on a drier world it is a playa. */
  lakeDepthR: 0.02, lakePastFloorR: 0.002, lakeVertices: 3, lakeWetness: -0.1,
  /** The flow (vertices upstream, the vertex itself counted) where a drainage chain starts, and the doublings to full order. */
  flowFrom: 12, flowDoublings: 3,
  /** The wet mask: hops from water where the nearness term is gone, and the terms' weights. */
  wetHops: 4, nearWater: 0.45, lowness: 0.25, lee: 0.15, lowland: 0.15,
  /** The climate cell's edges: wet at this moisture and up; high at this elevation band and up. */
  wetAt: 0.5, highAt: 0.6,
  /** Betweenness: this many seeded sites, paths walked between every pair; candidates kept this many hops apart; at most this many. */
  sites: 24, apartHops: 3, maxCandidates: 8,
  /**
   * The tighter rule (step 0's follow-up, the coordinator, Oct 30): a pass is a saddle whose notch is at least `passDepthPeople`
   * under the ridge on both sides and falls at least `passDropPeople` on both; a ford lies on a chain of order `fordOrder` and up;
   * either carries at least `concentration` × the mean paths of the vertices that carry any, and is the most-crossed vertex
   * within `concentrationHops` of itself (a corridor's many vertices collapse to its narrowest).
   */
  passDepthPeople: 3, passDropPeople: 1.5, fordOrder: 0.6, concentration: 3, concentrationHops: 2, notchHops: 3,
} as const;

export interface Basin {
  /** the sink vertex and its generated radius */
  sink: number; sinkRadius: number;
  /** the spill: the height a lake would fill to, and the rim vertex it spills over (−1 for the lowest basin of all: it never spills) */
  spillRadius: number; spillVertex: number;
  /** the vertices that drain to the sink */
  vertices: number[];
  /** depth under the spill (world units); whether it holds a lake (a deep basin on a world wet enough), or is a playa (the same basin on a dry world: a salt flat, no water) */
  depth: number; lake: boolean; playa: boolean;
}
export interface Chain { vertices: number[]; order: number; mouth: number }
export type ChokeKind = 'pass' | 'ford' | 'crossing';
export interface Chokepoint { vertex: number; kind: ChokeKind; score: number; paths: number; between: [number, number] }
export type ClimateCell = 'wetLow' | 'wetHigh' | 'dryLow' | 'dryHigh';

export interface WorldSheet {
  graph: PlanetRouteGraph;
  /** the generated radius per vertex, and the same with the pits filled (a hair's gradient toward each spill) */
  radius: Float64Array; filled: Float64Array;
  /** each vertex's way downhill (−1 at a sink), the flow through it, and the basin it drains to (an index into `basins`) */
  downhill: Int32Array; flow: Float64Array; basinOf: Int32Array;
  basins: Basin[]; chains: Chain[];
  /** 0–1 per vertex: the wet mask and the elevation band (the materials' normalised height) */
  moisture: Float32Array; elevation: Float32Array;
  /** the steepest grade to a neighbour (rise over run), per vertex */
  slope: Float32Array;
  cell: ClimateCell[];
  /** the candidates kept (ranked, apart, capped), how many scored before the cap, and how many of those are strong (within a quarter of the top score) */
  candidates: Chokepoint[]; candidatesScored: number; candidatesStrong: number;
  /** the saddles whose notch qualifies them as a pass by the ground alone, and the most paths any of them carries, as a multiple of the mean (what the route graph makes of them) */
  passSites: number; passBestShare: number;
  /** the spin axis (74.2 adopts it) and each vertex's latitude (rad, signed) */
  spinAxis: Vec3; latitude: Float32Array;
  /** what it cost to build (ms) */
  buildMs: number;
}

const sheets = new WeakMap<Float32Array, WorldSheet>();

/**
 * The planet's world sheet (built on first ask, cached by its generated heights). Nothing in it reads a seed: it used to be
 * keyed by the wind seed too, and the readers passing the match's seed and those passing none built the same sheet twice
 * per ground (a carved world's planned and carved ground each twice: ~34 ms of a Story or fhTug25 setup; on the owner's lab-38
 * 145 ms at setup and a 136 ms build in one tick of play, the first ride's roads asking for the carved ground's).
 */
export function worldSheetOf(planet: PlanetState): WorldSheet {
  const heights = planet.initialHeightmap ?? planet.heightmap;
  let s = sheets.get(heights);
  if (!s) { s = buildWorldSheet(planet); sheets.set(heights, s); }
  return s;
}

/**
 * 74.19: the sheet's first three steps — the basins and spills (pits filled), the flow, the drainage chains — on any
 * heights over the planet's weld (`radius`, one per weld vertex): the sheet's own on the generated ground
 * (`worldDrainageOf`), and erosion carving's on the ground as it stands when it carves (core/erosionCarve.ts; inside
 * the landform pass, the base field and the landforms' layer together). Pure; the sheet's chokepoints are not built here.
 * 74.22 stage 2: `weight`, one per weld vertex, is what each vertex sheds into the flow (`firstGuessRain`'s `weight`, mean 1,
 * so `flowFrom` and the beds' classes keep their meaning on average); absent, one unit a vertex — today's flow, bit for bit.
 */
export function drainageOn(planet: Pick<PlanetState, 'radius' | 'archetype'>, graph: PlanetRouteGraph, radius: Float64Array, weight?: Float64Array): Drainage {
  const V = graph.count, R = planet.radius;
  const nbrs = (v: number): Uint32Array => graph.nbr.subarray(graph.start[v], graph.start[v + 1]);

  // ---- 1. basins and spills: ascending height, union-find; pits filled -------------------------------------------------
  const order = new Int32Array(V);
  for (let v = 0; v < V; v++) order[v] = v;
  order.sort((a, b) => (radius[a] - radius[b]) || (a - b));
  const rank = new Int32Array(V);
  for (let k = 0; k < V; k++) rank[order[k]] = k;
  const parent = new Int32Array(V), rootSink = new Int32Array(V), members: number[][] = [];
  const find = (v: number): number => { while (parent[v] !== v) { parent[v] = parent[parent[v]]; v = parent[v]; } return v; };
  const filled = Float64Array.from(radius);
  const basins: Basin[] = [];
  const basinOf = new Int32Array(V).fill(-1);
  // the pit floor: the generator's hashed jitter (TERRAIN_PROFILES: jitter × amplitude × R of height) digs pits of its own; those are filled
  const profile = TERRAIN_PROFILES[planet.archetype as PlanetArchetype];
  const jitterHeight = profile ? profile.jitter * profile.amplitude * R : 0;
  const pitFloor = Math.max(WORLD_SHEET.pitFloorR * R, WORLD_SHEET.pitJitter * jitterHeight);
  const wetWorld = (ARCHETYPE_WETNESS[planet.archetype as PlanetArchetype] ?? 0) >= WORLD_SHEET.lakeWetness;   // (341: lakes where the world is wet; a dry world's deep basin is a playa)
  const lakeDepth = Math.max(WORLD_SHEET.lakeDepthR * R, pitFloor + WORLD_SHEET.lakePastFloorR * R);
  for (let k = 0; k < V; k++) {
    const v = order[k];
    const lower: number[] = [];
    for (const n of nbrs(v)) if (rank[n] < k) { const r = find(n); if (!lower.includes(r)) lower.push(r); }
    if (lower.length === 0) { parent[v] = v; rootSink[v] = v; members[v] = [v]; continue; }
    // the keeper: the basin with the lowest sink (ties by sink index); the others spill into it here
    let keep = lower[0];
    for (const r of lower) if (radius[rootSink[r]] < radius[rootSink[keep]] || (radius[rootSink[r]] === radius[rootSink[keep]] && rootSink[r] < rootSink[keep])) keep = r;
    for (const r of lower) {
      if (r === keep) continue;
      const sink = rootSink[r], depth = radius[v] - radius[sink];
      if (depth < pitFloor) {
        // a pit: filled to its spill, with a hair's gradient toward the spill so it drains (BFS from the spill over its members)
        fillPit(members[r], v, radius[v], filled, nbrs);
      } else {
        const b: Basin = { sink, sinkRadius: radius[sink], spillRadius: radius[v], spillVertex: v, vertices: members[r].filter(m => basinOf[m] < 0), depth, lake: false, playa: false };   // (a basin inside it keeps its own)
        const deep = depth >= lakeDepth && b.vertices.length >= WORLD_SHEET.lakeVertices;
        b.lake = deep && wetWorld; b.playa = deep && !wetWorld;
        for (const m of b.vertices) if (basinOf[m] < 0) basinOf[m] = basins.length;
        basins.push(b);
      }
      parent[r] = keep;
      members[keep].push(...members[r]); members[r] = [];
    }
    parent[v] = keep; members[keep].push(v);
  }
  // the lowest basin of all never spills: the rest of the vertices are its
  const roots = new Set<number>();
  for (let v = 0; v < V; v++) roots.add(find(v));
  for (const r of roots) {
    const sink = rootSink[r], vertices = members[r].filter(m => basinOf[m] < 0);
    const b: Basin = { sink, sinkRadius: radius[sink], spillRadius: Infinity, spillVertex: -1, vertices, depth: Infinity, lake: false, playa: false };
    for (const m of vertices) basinOf[m] = basins.length;
    basins.push(b);
  }

  // ---- 2. the flow: each vertex to its lowest filled neighbour; accumulated in descending height ----------------------
  const downhill = new Int32Array(V).fill(-1);
  for (let v = 0; v < V; v++) {
    let best = -1;
    for (const n of nbrs(v)) if (filled[n] < filled[v]) { if (best < 0 || filled[n] < filled[best] || (filled[n] === filled[best] && n < best)) best = n; }
    downhill[v] = best;
  }
  if (weight && weight.length !== V) throw new Error(`drainageOn: ${weight.length} weights for ${V} weld vertices`);
  const flow = weight ? Float64Array.from(weight) : new Float64Array(V).fill(1);
  const byFilled = new Int32Array(V);
  for (let v = 0; v < V; v++) byFilled[v] = v;
  byFilled.sort((a, b) => (filled[b] - filled[a]) || (a - b));
  for (const v of byFilled) if (downhill[v] >= 0) flow[downhill[v]] += flow[v];

  // ---- 3. the chains: from each source (flow past the threshold, no such upstream) down to a mouth ----------------------
  const upstreamStrong = new Uint8Array(V);
  for (let v = 0; v < V; v++) if (flow[v] >= WORLD_SHEET.flowFrom && downhill[v] >= 0) upstreamStrong[downhill[v]] = 1;
  const inChain = new Int32Array(V).fill(-1);
  const chains: Chain[] = [];
  const sources: number[] = [];
  for (let v = 0; v < V; v++) if (flow[v] >= WORLD_SHEET.flowFrom && !upstreamStrong[v]) sources.push(v);
  sources.sort((a, b) => (flow[b] - flow[a]) || (a - b));   // the strongest first, so a confluence joins the bigger chain
  for (const s of sources) {
    const vertices: number[] = [];
    let v = s;
    while (v >= 0 && inChain[v] < 0) { vertices.push(v); inChain[v] = chains.length; v = downhill[v]; }
    if (v >= 0) vertices.push(v);   // the mouth: the sink, or a vertex of a chain already laid
    const peak = flow[vertices[vertices.length - 1]];
    chains.push({ vertices, order: Math.min(1, log2(peak / WORLD_SHEET.flowFrom) / WORLD_SHEET.flowDoublings), mouth: v });
  }

  return { graph, radius, filled, basins, basinOf, downhill, flow, chains, inChain };
}

/** The drainage of the generated ground (the sheet's steps 1–3), cached by the weld (itself cached by the generated heights). */
export function worldDrainageOf(planet: PlanetState): Drainage {
  const graph = planetRouteGraph(planet);
  let d = drainages.get(graph);
  if (!d) { d = drainageOn(planet, graph, graph.radii); drainages.set(graph, d); }
  return d;
}
const drainages = new WeakMap<PlanetRouteGraph, Drainage>();

/**
 * 74.22 stage 2 (DECISIONS 443; ARCHITECTURE §5.53 §5 (2), (3)): the **first-guess rainfall** — what falls on each weld
 * vertex before the climate solve knows the ground: the archetype's wetness and the latitude's belts, nothing else. The
 * landforms' stream-power K reads `rain` (a wet range erodes more than a dry one); the carve and the sheet weight their
 * flow by `weight` (so a wet range's valleys are the deeper ones, and the beds' classes follow the valley the carve cut).
 *
 * The latitude, about the spin axis (`spinAxisOf`, the bands' axis): `level + belt · cos 6φ − polar · (1 − cos φ)`, floored
 * at `floor` — wet at the equator (where the easterlies meet) and at 60° (the westerlies' fronts), dry at 30° and the
 * poles (the bands' descending air, and cold air holds little water), as WIND_FIELD's three bands a hemisphere blow.
 * `level` is `1 + wetnessGain · ARCHETYPE_WETNESS`: on a dry world the belts are the same height over a lower level, so
 * its subtropics reach the floor (a desert belt) and its rain is the more concentrated. cos 6φ is T₆(cos φ), a
 * polynomial: no trig.
 *
 * `rain` is in one scale for every archetype (the sphere's mean: viridis 1.2, thalia 0.9, ochrus 0.36, sonora 0.2); `weight` is `rain` over its mean on the planet's
 * vertices (the weld's vertices are near-uniform in area), so its mean is 1. Pure; cached on the weld, keyed by the
 * archetype and the axis (every input). Nothing in the game reads it yet: the carve and the sheet switch to it behind
 * stage 2's flag.
 */
export const FIRST_GUESS_RAIN = { wetnessGain: 2.5, belt: 0.3, polar: 0.5, floor: 0.05 } as const;

export interface FirstGuessRain {
  /** per weld vertex: the rainfall in the archetype's own units, and the same over its planet mean (mean 1) */
  rain: Float64Array; weight: Float64Array;
  /** the planet mean of `rain` */
  mean: number;
}

export type RainPlanet = Pick<PlanetState, 'archetype' | 'id' | 'heightmap' | 'initialHeightmap' | 'baseGroundKey' | 'carvedGroundKey'>;

/** The rainfall at latitude sine `s` (the direction's component along the spin axis) for an archetype's wetness. Pure. */
export function rainAtLatitude(s: number, wetness: number): number {
  const F = FIRST_GUESS_RAIN;
  const c2 = Math.max(0, 1 - s * s), c = Math.sqrt(c2);
  const cos6 = ((32 * c2 - 48) * c2 + 18) * c2 - 1;   // T₆(cos φ) = cos 6φ
  return Math.max(F.floor, 1 + F.wetnessGain * wetness + F.belt * cos6 - F.polar * (1 - c));
}

export function firstGuessRain(planet: RainPlanet, graph: PlanetRouteGraph): FirstGuessRain {
  const axis = spinAxisOf(planet), wetness = ARCHETYPE_WETNESS[planet.archetype as PlanetArchetype] ?? 0;
  const key = `${planet.archetype}|${axis.x}|${axis.y}|${axis.z}`;
  let byKey = rains.get(graph);
  if (!byKey) { byKey = new Map(); rains.set(graph, byKey); }
  let r = byKey.get(key);
  if (!r) {
    const V = graph.count, d = graph.dirs, rain = new Float64Array(V);
    let sum = 0;
    for (let v = 0; v < V; v++) { rain[v] = rainAtLatitude(d[3 * v] * axis.x + d[3 * v + 1] * axis.y + d[3 * v + 2] * axis.z, wetness); sum += rain[v]; }
    const mean = V > 0 ? sum / V : 1, weight = new Float64Array(V);
    for (let v = 0; v < V; v++) weight[v] = rain[v] / mean;
    r = { rain, weight, mean };
    byKey.set(key, r);
  }
  return r;
}
const rains = new WeakMap<PlanetRouteGraph, Map<string, FirstGuessRain>>();

/** The sheet's steps 1–3: the heights it read, the pit-filled heights, the basins, each vertex's way down and flow, the chains (and each vertex's chain, −1 none). */
export interface Drainage {
  graph: PlanetRouteGraph; radius: Float64Array; filled: Float64Array; basins: Basin[]; basinOf: Int32Array;
  downhill: Int32Array; flow: Float64Array; chains: Chain[]; inChain: Int32Array;
}
/** The sheet, built (pure). */
export function buildWorldSheet(planet: PlanetState): WorldSheet {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
  const graph = planetRouteGraph(planet), V = graph.count, R = planet.radius;
  const radius = graph.radii;
  const nbrs = (v: number): Uint32Array => graph.nbr.subarray(graph.start[v], graph.start[v + 1]);

  const { filled, basins, basinOf, downhill, flow, chains, inChain } = worldDrainageOf(planet);

  // ---- 4. the wet mask, the elevation band, the slope, the latitude ----------------------------------------------------
  let lo = Infinity, hi = -Infinity;
  for (let v = 0; v < V; v++) { lo = Math.min(lo, radius[v]); hi = Math.max(hi, radius[v]); }
  const elevation = new Float32Array(V), slope = new Float32Array(V), moisture = new Float32Array(V), latitude = new Float32Array(V);
  const axisV = windAxis(planet), spinAxis = spinAxisOf(planet);
  // hops from water: a BFS from every chain vertex and lake vertex at once
  const hops = new Int32Array(V).fill(-1);
  const queue: number[] = [];
  for (let v = 0; v < V; v++) if (inChain[v] >= 0 || basins[basinOf[v]].lake) { hops[v] = 0; queue.push(v); }
  for (let q = 0; q < queue.length; q++) { const v = queue[q]; if (hops[v] >= WORLD_SHEET.wetHops) continue; for (const n of nbrs(v)) if (hops[n] < 0) { hops[n] = hops[v] + 1; queue.push(n); } }
  for (let v = 0; v < V; v++) {
    const d = graph.dirs, x = d[3 * v], y = d[3 * v + 1], z = d[3 * v + 2];
    elevation[v] = hi > lo ? (radius[v] - lo) / (hi - lo) : 0.5;
    let steep = 0, gx = 0, gy = 0, gz = 0;
    for (const n of nbrs(v)) {
      const run = chord(graph, v, n) * R, rise = radius[n] - radius[v];
      if (run > 0) { steep = Math.max(steep, Math.abs(rise) / run); gx += (d[3 * n] - x) * rise; gy += (d[3 * n + 1] - y) * rise; gz += (d[3 * n + 2] - z) * rise; }
    }
    slope[v] = steep;
    // lee: the ground falls away along the wind (the gradient against the wind's direction here)
    const wind = windHere(axisV, x, y, z), gl = Math.sqrt(gx * gx + gy * gy + gz * gz) || 1;
    const leeTerm = 0.5 + 0.5 * (-(gx * wind.x + gy * wind.y + gz * wind.z) / gl);
    const near = hops[v] < 0 ? 0 : 1 - hops[v] / WORLD_SHEET.wetHops;
    const b = basins[basinOf[v]], lowness = Number.isFinite(b.depth) ? Math.max(0, Math.min(1, (b.spillRadius - radius[v]) / Math.max(1e-9, b.depth))) : Math.max(0, Math.min(1, 1 - elevation[v]));
    moisture[v] = Math.max(0, Math.min(1, WORLD_SHEET.nearWater * near + WORLD_SHEET.lowness * lowness + WORLD_SHEET.lee * leeTerm + WORLD_SHEET.lowland * (1 - elevation[v])));
    latitude[v] = dasin(Math.max(-1, Math.min(1, x * spinAxis.x + y * spinAxis.y + z * spinAxis.z)));
  }
  // the archetype's own climate (341: the sun's distance joins it as one more offset when 74.1's system reaches the planet)
  const dry = ARCHETYPE_WETNESS[planet.archetype as PlanetArchetype] ?? 0;
  for (let v = 0; v < V; v++) moisture[v] = Math.max(0, Math.min(1, moisture[v] + dry));
  const cell: ClimateCell[] = new Array(V);
  for (let v = 0; v < V; v++) cell[v] = `${moisture[v] >= WORLD_SHEET.wetAt ? 'wet' : 'dry'}${elevation[v] >= WORLD_SHEET.highAt ? 'High' : 'Low'}` as ClimateCell;

  // ---- 5. chokepoint candidates: saddles between basins, fords, and the vertices the most paths cross -------------------
  const paths = new Float64Array(V);
  const siteVerts = seededSites(planet, graph, WORLD_SHEET.sites);
  for (const dv of siteVerts) {
    const field = routeField(planet, { x: graph.dirs[3 * dv], y: graph.dirs[3 * dv + 1], z: graph.dirs[3 * dv + 2] }, 'wheels');   // (on wheels: steep ground is refused, so the ground channels the paths — on foot nearly nothing is, and a route crosses a ridge anywhere)
    for (const sv of siteVerts) {
      if (sv === dv) continue;
      let v = sv, guard = 0;
      while (v >= 0 && v !== dv && guard++ < V) { paths[v]++; v = field.next[v]; }
    }
  }
  // the people's height in world units (the sheet is read at the human scale)
  const person = PERSON_BODY_HEIGHT * SCALE_PROFILES.human.kHuman;
  let carried = 0, carriers = 0;
  for (let v = 0; v < V; v++) if (paths[v] > 0) { carried += paths[v]; carriers++; }
  const meanPaths = carriers ? carried / carriers : 0;
  // each vertex's kind by its ground alone: a pass (a saddle with a notch deep enough) or a ford (a strong chain's vertex)
  const kindOf = new Array<ChokeKind | null>(V).fill(null);
  for (let v = 0; v < V; v++) {
    if (paths[v] <= 0) continue;
    const notch = saddleNotch(graph, radius, v, WORLD_SHEET.notchHops);
    if (notch && notch.depth >= WORLD_SHEET.passDepthPeople * person && notch.drop >= WORLD_SHEET.passDropPeople * person) kindOf[v] = 'pass';
    else if (inChain[v] >= 0 && chains[inChain[v]].order >= WORLD_SHEET.fordOrder) kindOf[v] = 'ford';
  }
  let passSites = 0, passBest = 0;
  for (let v = 0; v < V; v++) if (kindOf[v] === 'pass') { passSites++; passBest = Math.max(passBest, paths[v]); }
  for (let v = 0; v < V; v++) if (paths[v] <= 0) { const notch = saddleNotch(graph, radius, v, WORLD_SHEET.notchHops); if (notch && notch.depth >= WORLD_SHEET.passDepthPeople * person && notch.drop >= WORLD_SHEET.passDropPeople * person) passSites++; }
  const passBestShare = meanPaths > 0 ? passBest / meanPaths : 0;
  // the concentration: carries its share of the paths, and is the most-crossed *of its kind* within reach (the shortest paths
  // run along the valley floors, so judged against every vertex a chain's vertex would always beat the saddle beside it)
  const scored: Chokepoint[] = [];
  for (let v = 0; v < V; v++) {
    const kind = kindOf[v];
    if (!kind || paths[v] < WORLD_SHEET.concentration * meanPaths) continue;
    let localMax = true;
    for (const n of withinHops(graph, v, WORLD_SHEET.concentrationHops)) if (kindOf[n] === kind && (paths[n] > paths[v] || (paths[n] === paths[v] && n < v))) { localMax = false; break; }
    if (!localMax) continue;
    const between = betweenOf(graph, basinOf, v);
    if (kind === 'pass' && between[0] === between[1]) continue;
    scored.push({ vertex: v, kind, score: paths[v] * (kind === 'pass' ? 1.5 : 1), paths: paths[v], between });
  }
  scored.sort((a, b) => (b.score - a.score) || (a.vertex - b.vertex));
  const top = scored[0]?.score ?? 0, strong = scored.filter(c => c.score >= 0.25 * top).length;
  const candidates: Chokepoint[] = [];
  for (const c of scored) {
    if (candidates.length >= WORLD_SHEET.maxCandidates) break;
    if (candidates.some(k => hopsBetween(graph, k.vertex, c.vertex, WORLD_SHEET.apartHops) <= WORLD_SHEET.apartHops)) continue;
    candidates.push(c);
  }

  const buildMs = typeof performance !== 'undefined' ? performance.now() - t0 : 0;
  return { graph, radius, filled, downhill, flow, basinOf, basins, chains, moisture, elevation, slope, cell, candidates, candidatesScored: scored.length, candidatesStrong: strong, passSites, passBestShare, spinAxis, latitude, buildMs };
}

// ---- helpers --------------------------------------------------------------------------------------------------------------

/** log2 by halving (dmath has no log2; the ratio is a count's, so exactness is a vertex's worth of order). */
function log2(x: number): number { let n = 0; while (x >= 2) { x /= 2; n++; } return n + (x - 1); }

function chord(g: PlanetRouteGraph, a: number, b: number): number {
  const d = g.dirs, dx = d[3 * a] - d[3 * b], dy = d[3 * a + 1] - d[3 * b + 1], dz = d[3 * a + 2] - d[3 * b + 2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/** A pit's members raised to its spill, each a hair higher the further from the spill, so it drains to it. */
function fillPit(members: number[], spill: number, level: number, filled: Float64Array, nbrs: (v: number) => Uint32Array): void {
  const inPit = new Set(members);
  const depth = new Map<number, number>([[spill, 0]]);
  const queue = [spill];
  for (let q = 0; q < queue.length; q++) for (const n of nbrs(queue[q])) if (inPit.has(n) && !depth.has(n)) { depth.set(n, depth.get(queue[q])! + 1); queue.push(n); }
  const eps = level * 1e-9;
  for (const m of members) filled[m] = Math.max(filled[m], level + eps * (depth.get(m) ?? members.length));
}

/** The wind's direction at a point: the axis's east (the zonal flow), as core/windField.ts lays the bands. */
function windHere(axis: Vec3, x: number, y: number, z: number): Vec3 {
  const ex = axis.y * z - axis.z * y, ey = axis.z * x - axis.x * z, ez = axis.x * y - axis.y * x;
  const l = Math.sqrt(ex * ex + ey * ey + ez * ez) || 1;
  return { x: ex / l, y: ey / l, z: ez / l };
}

/** The vertices within `hops` of `v` (not `v` itself). */
function withinHops(g: PlanetRouteGraph, v: number, hops: number): number[] {
  const seen = new Set([v]); let frontier = [v]; const out: number[] = [];
  for (let h = 0; h < hops; h++) {
    const next: number[] = [];
    for (const u of frontier) for (let k = g.start[u]; k < g.start[u + 1]; k++) { const n = g.nbr[k]; if (!seen.has(n)) { seen.add(n); next.push(n); out.push(n); } }
    frontier = next;
  }
  return out;
}

/**
 * A saddle's notch: round the vertex's ring of neighbours (by bearing) the height against its own changes sign four times, two
 * arcs up (the ridge either side) and two down (the ways through). Its `depth` is the lesser rise to the two ridges' crests and
 * its `drop` the lesser fall to the two ways' floors, each read over the vertices within `hops` of the saddle whose bearing
 * falls in that arc (the mesh's edge is 12–50 people, so one ring sees only the first step of a ridge); null where the ring is
 * no saddle.
 */
function saddleNotch(g: PlanetRouteGraph, radius: Float64Array, v: number, hops: number): { depth: number; drop: number } | null {
  const d = g.dirs, x = d[3 * v], y = d[3 * v + 1], z = d[3 * v + 2];
  const ref = Math.abs(y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const e1 = norm({ x: ref.y * z - ref.z * y, y: ref.z * x - ref.x * z, z: ref.x * y - ref.y * x });
  const e2 = { x: y * e1.z - z * e1.y, y: z * e1.x - x * e1.z, z: x * e1.y - y * e1.x };
  const bearingOf = (n: number): number => { const dx = d[3 * n] - x, dy = d[3 * n + 1] - y, dz = d[3 * n + 2] - z; return datan2(dx * e2.x + dy * e2.y + dz * e2.z, dx * e1.x + dy * e1.y + dz * e1.z); };
  const ring: Array<{ bearing: number; up: boolean }> = [];
  for (let k = g.start[v]; k < g.start[v + 1]; k++) { const n = g.nbr[k]; ring.push({ bearing: bearingOf(n), up: radius[n] > radius[v] }); }
  if (ring.length < 4) return null;
  ring.sort((a, b) => a.bearing - b.bearing);
  // the arcs: runs of the same sign round the ring, each a bearing interval from its first member to the next arc's first
  let start = 0;
  while (start < ring.length && ring[start].up === ring[(start + ring.length - 1) % ring.length].up) start++;
  if (start === ring.length) return null;
  const arcs: Array<{ up: boolean; from: number; to: number }> = [];
  for (let i = 0; i < ring.length; i++) {
    const r = ring[(start + i) % ring.length];
    const last = arcs[arcs.length - 1];
    if (!last || last.up !== r.up) arcs.push({ up: r.up, from: r.bearing, to: r.bearing });
  }
  if (arcs.length !== 4) return null;
  for (let i = 0; i < 4; i++) arcs[i].to = arcs[(i + 1) % 4].from;   // (an arc runs from its first member's bearing to the next arc's first)
  const inArc = (b: number, a: { from: number; to: number }): boolean => { let t = b - a.from, span = a.to - a.from; if (span <= 0) span += Math.PI * 2; while (t < 0) t += Math.PI * 2; while (t >= Math.PI * 2) t -= Math.PI * 2; return t < span; };
  // the crests and floors within `hops`, by arc
  const crest = [-Infinity, -Infinity], floor = [Infinity, Infinity];
  let upI = 0, downI = 0;
  const arcIndex = arcs.map(a => a.up ? upI++ : downI++);
  for (const n of withinHops(g, v, hops)) {
    const b = bearingOf(n), rise = radius[n] - radius[v];
    for (let i = 0; i < 4; i++) if (inArc(b, arcs[i])) { if (arcs[i].up) crest[arcIndex[i]] = Math.max(crest[arcIndex[i]], rise); else floor[arcIndex[i]] = Math.min(floor[arcIndex[i]], rise); break; }
  }
  if (!Number.isFinite(crest[0]) || !Number.isFinite(crest[1]) || !Number.isFinite(floor[0]) || !Number.isFinite(floor[1])) return null;
  return { depth: Math.min(crest[0], crest[1]), drop: Math.min(-floor[0], -floor[1]) };
}

/** A saddle (kept for the tests and the table): round the vertex's ring of neighbours (by bearing), the height against its own changes sign four times. */
export function isSaddle(g: PlanetRouteGraph, radius: Float64Array, v: number): boolean {
  const d = g.dirs, x = d[3 * v], y = d[3 * v + 1], z = d[3 * v + 2];
  const ref = Math.abs(y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const e1 = norm({ x: ref.y * z - ref.z * y, y: ref.z * x - ref.x * z, z: ref.x * y - ref.y * x });
  const e2 = { x: y * e1.z - z * e1.y, y: z * e1.x - x * e1.z, z: x * e1.y - y * e1.x };
  const ring: Array<{ bearing: number; up: boolean }> = [];
  for (let k = g.start[v]; k < g.start[v + 1]; k++) {
    const n = g.nbr[k], dx = d[3 * n] - x, dy = d[3 * n + 1] - y, dz = d[3 * n + 2] - z;
    ring.push({ bearing: datan2(dx * e2.x + dy * e2.y + dz * e2.z, dx * e1.x + dy * e1.y + dz * e1.z), up: radius[n] > radius[v] });
  }
  if (ring.length < 4) return false;
  ring.sort((a, b) => a.bearing - b.bearing);
  let changes = 0;
  for (let i = 0; i < ring.length; i++) if (ring[i].up !== ring[(i + 1) % ring.length].up) changes++;
  return changes >= 4;
}
function norm(v: Vec3): Vec3 { const l = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; }

/** The two basins a vertex stands between: its own and the other one most of its neighbours drain to (its own twice when there is none). */
function betweenOf(g: PlanetRouteGraph, basinOf: Int32Array, v: number): [number, number] {
  const own = basinOf[v], counts = new Map<number, number>();
  for (let k = g.start[v]; k < g.start[v + 1]; k++) { const b = basinOf[g.nbr[k]]; if (b !== own) counts.set(b, (counts.get(b) ?? 0) + 1); }
  let other = own, best = 0;
  for (const [b, c] of counts) if (c > best || (c === best && b < other)) { other = b; best = c; }
  return [Math.min(own, other), Math.max(own, other)];
}

/** Hops from `a` to `b` by BFS, stopping past `limit` (returns limit + 1). */
function hopsBetween(g: PlanetRouteGraph, a: number, b: number, limit: number): number {
  if (a === b) return 0;
  let frontier = [a]; const seen = new Set([a]);
  for (let h = 1; h <= limit; h++) {
    const next: number[] = [];
    for (const v of frontier) for (let k = g.start[v]; k < g.start[v + 1]; k++) { const n = g.nbr[k]; if (n === b) return h; if (!seen.has(n)) { seen.add(n); next.push(n); } }
    frontier = next;
  }
  return limit + 1;
}

/** `count` sites spread over the planet (a seeded Fibonacci lattice, turned by the seed), each the nearest vertex. */
function seededSites(planet: PlanetState, g: PlanetRouteGraph, count: number): number[] {
  const rng = new SeededRandom(`${groundKey(planet)}-sheet-sites`);
  const turn = rng.range(0, Math.PI * 2), golden = Math.PI * (3 - Math.sqrt(5));
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const y = 1 - (2 * (i + 0.5)) / count, r = Math.sqrt(Math.max(0, 1 - y * y)), a = golden * i + turn;
    const x = r * dcos(a), z = r * dsin(a);
    let best = 0, bestDot = -2;
    for (let v = 0; v < g.count; v++) { const dot = g.dirs[3 * v] * x + g.dirs[3 * v + 1] * y + g.dirs[3 * v + 2] * z; if (dot > bestDot) { bestDot = dot; best = v; } }
    if (!out.includes(best)) out.push(best);
  }
  return out;
}

/** Per archetype, a shift of the wet mask: the arid and ash worlds dry, the overgrown and tidal ones wet (341's climate; the sun's distance adds to it later). */
export const ARCHETYPE_WETNESS: Record<PlanetArchetype, number> = { thalia: 0, viridis: 0.12, pelago: 0.1, bruma: -0.05, pallor: -0.25, ochrus: -0.22, tephra: -0.18, murex: -0.05, sonora: -0.3 };

/** Per archetype, the biome the field's cell grows (341: biomes by wetness × height; today's ids as the cells, 70.5's families later). */
export const CLIMATE_BIOMES: Record<PlanetArchetype, Record<ClimateCell, import('../types').BiomeId | null>> = {
  thalia: { wetLow: 'broadleaf', wetHigh: 'firStand', dryLow: 'heath', dryHigh: null },
  viridis: { wetLow: 'broadleaf', wetHigh: 'firStand', dryLow: 'birchGlade', dryHigh: 'heath' },
  pelago: { wetLow: 'broadleaf', wetHigh: 'heath', dryLow: 'scrub', dryHigh: null },
  bruma: { wetLow: 'firStand', wetHigh: null, dryLow: 'birchGlade', dryHigh: null },
  pallor: { wetLow: 'scrub', wetHigh: 'heath', dryLow: null, dryHigh: null },
  ochrus: { wetLow: 'savanna', wetHigh: 'scrub', dryLow: null, dryHigh: null },
  tephra: { wetLow: 'ashBarrens', wetHigh: 'burn', dryLow: 'ashBarrens', dryHigh: null },
  murex: { wetLow: 'lanternGrove', wetHigh: 'lanternGrove', dryLow: 'scrub', dryHigh: null },
  sonora: { wetLow: 'hollowWood', wetHigh: null, dryLow: 'hollowWood', dryHigh: null },
};

/** A summary for the sweep and the table's caption. */
export function sheetSummary(s: WorldSheet): { vertices: number; basins: number; lakes: number; playas: number; chains: number; chainVertices: number; wetShare: number; candidates: number; scored: number; strong: number; passes: number; fords: number; passSites: number; passBestShare: number; buildMs: number } {
  let chainVertices = 0, wet = 0;
  for (const c of s.chains) chainVertices += c.vertices.length;
  for (let v = 0; v < s.graph.count; v++) if (s.moisture[v] >= WORLD_SHEET.wetAt) wet++;
  return {
    vertices: s.graph.count, basins: s.basins.length, lakes: s.basins.filter(b => b.lake).length, playas: s.basins.filter(b => b.playa).length, chains: s.chains.length, chainVertices,
    wetShare: wet / s.graph.count, candidates: s.candidates.length, scored: s.candidatesScored, strong: s.candidatesStrong, passes: s.candidates.filter(c => c.kind === 'pass').length, fords: s.candidates.filter(c => c.kind === 'ford').length, passSites: s.passSites, passBestShare: s.passBestShare, buildMs: s.buildMs,
  };
}
/**
 * A lake's outline for a drawing: the basin's vertices under its level that touch one above it, in order round the lake's
 * middle (by bearing in the tangent plane) — a rough shore, enough for the table.
 */
export function lakeOutline(s: WorldSheet, b: Basin): number[] {
  const g = s.graph, under = new Set(b.vertices.filter(v => s.radius[v] <= b.spillRadius));
  const shore: number[] = [];
  for (const v of under) { let edge = false; for (let k = g.start[v]; k < g.start[v + 1]; k++) if (!under.has(g.nbr[k])) { edge = true; break; } if (edge) shore.push(v); }
  if (shore.length < 3) return shore;
  let cx = 0, cy = 0, cz = 0;
  for (const v of shore) { cx += g.dirs[3 * v]; cy += g.dirs[3 * v + 1]; cz += g.dirs[3 * v + 2]; }
  const c = norm({ x: cx, y: cy, z: cz });
  const ref = Math.abs(c.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const e1 = norm({ x: ref.y * c.z - ref.z * c.y, y: ref.z * c.x - ref.x * c.z, z: ref.x * c.y - ref.y * c.x });
  const e2 = { x: c.y * e1.z - c.z * e1.y, y: c.z * e1.x - c.x * e1.z, z: c.x * e1.y - c.y * e1.x };
  const bearing = (v: number): number => { const dx = g.dirs[3 * v] - c.x, dy = g.dirs[3 * v + 1] - c.y, dz = g.dirs[3 * v + 2] - c.z; return datan2(dx * e2.x + dy * e2.y + dz * e2.z, dx * e1.x + dy * e1.y + dz * e1.z); };
  return shore.sort((a, b2) => bearing(a) - bearing(b2) || a - b2);
}
